import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Запись голосового в браузере — как в чате сайта (InputChatUI): MediaRecorder,
 * обычно webm/opus. Сервер принимает webm/ogg/m4a и сам расшифровывает запись
 * (так же, как голосовые из приложения).
 *
 * start() — начать; finish() — остановить и получить { blob, durationMs, mimeType };
 * cancel() — выбросить запись.
 */
export default function useWebVoiceRecorder() {
    const recorderRef = useRef(null);
    const streamRef = useRef(null);
    const chunksRef = useRef([]);
    const startedAtRef = useRef(0);
    const timerRef = useRef(null);
    const resolveRef = useRef(null);
    const cancelledRef = useRef(false);

    const [recording, setRecording] = useState(false);
    const [seconds, setSeconds] = useState(0);
    const [error, setError] = useState("");

    const cleanup = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
        recorderRef.current = null;
        chunksRef.current = [];
        setRecording(false);
        setSeconds(0);
    }, []);

    useEffect(() => () => {
        cancelledRef.current = true;
        try {
            if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
        } catch (_error) { }
        cleanup();
    }, [cleanup]);

    const start = useCallback(async () => {
        if (recorderRef.current) return false;

        if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
            setError("Этот браузер не умеет записывать голос. Попробуйте Chrome или запишите в приложении.");
            return false;
        }

        try {
            setError("");
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            chunksRef.current = [];
            cancelledRef.current = false;
            startedAtRef.current = Date.now();

            const recorder = new MediaRecorder(stream);
            recorderRef.current = recorder;

            recorder.ondataavailable = (event) => {
                if (event.data?.size) chunksRef.current.push(event.data);
            };

            recorder.onstop = () => {
                const durationMs = Date.now() - startedAtRef.current;
                const mimeType = recorder.mimeType || "audio/webm";
                const blob = new Blob(chunksRef.current, { type: mimeType });
                const resolve = resolveRef.current;
                resolveRef.current = null;
                const cancelled = cancelledRef.current;
                cleanup();
                if (resolve) resolve(!cancelled && blob.size > 0 ? { blob, durationMs, mimeType } : null);
            };

            recorder.start();
            setRecording(true);
            setSeconds(0);
            timerRef.current = setInterval(() => {
                setSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000));
            }, 300);
            return true;
        } catch (_error) {
            cleanup();
            setError("Нет доступа к микрофону. Разрешите микрофон для сайта в настройках браузера.");
            return false;
        }
    }, [cleanup]);

    const stopWith = useCallback((cancel) => new Promise((resolve) => {
        const recorder = recorderRef.current;
        if (!recorder || recorder.state === "inactive") {
            resolve(null);
            return;
        }
        cancelledRef.current = cancel;
        resolveRef.current = resolve;
        recorder.stop();
    }), []);

    const finish = useCallback(() => stopWith(false), [stopWith]);
    const cancel = useCallback(() => stopWith(true), [stopWith]);

    return { recording, seconds, error, setError, start, finish, cancel };
}

export const formatSeconds = (value) => {
    const total = Math.max(0, Number(value || 0));
    return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
};

/** Расширение файла по типу записи — сервер по нему понимает формат. */
export const voiceFileName = (mimeType = "") => {
    const mime = String(mimeType).toLowerCase();
    const ext = mime.includes("ogg") ? "ogg" : mime.includes("mp4") || mime.includes("aac") ? "m4a" : mime.includes("wav") ? "wav" : "webm";
    return `voice-${Date.now()}.${ext}`;
};
